import React from 'react';
import PropTypes from 'prop-types';
import ReactSelect from 'react-select';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Button, Typography } from '@material-ui/core';
import {
  NoOptionsMessage,
  Control,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
  MultiValue,
  Menu,
} from 'components/Forms/CustomSelect/selectComponents';

const selectComponents = {
  Control,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
};

const useStyles = makeStyles(theme => ({
  root: {
    '& label': {
      lineHeight: '30px',
    },
    '& label + .MuiInput-formControl': {
      marginTop: '30px',
    },
    '& .MuiInput-underline.Mui-error:after': {
      border: `1px solid ${theme.palette.error.main}`,
    },
    '& .MuiInput-underline:before': {
      display: 'none',
    },
    '& .MuiInput-underline:after': {
      top: 0,
      border: `2px solid ${theme.palette.primary.main}`,
      borderRadius: '25px',
      transition: 'unset',
    },
  },
  input: {
    display: 'flex',
    height: 'unset',
    padding: '2.5px 14px 2.5px 10px',
    border: '1px solid #d3d3d3',
    borderRadius: '25px',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  valueContainer: {
    minWidth: '100px',
    display: 'flex',
    flexWrap: 'wrap',
    flex: 1,
    alignItems: 'center',
    overflow: 'hidden',
  },
  chip: {
    margin: theme.spacing(0.5, 0.2),
    background: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    [theme.breakpoints.down('lg')]: {
      maxWidth: '100%',
    },
    [theme.breakpoints.up('lg')]: {
      maxWidth: '165px',
    },
  },
  noOptionsMessage: {
    padding: theme.spacing(1, 2),
  },
  singleValue: {
    fontSize: 16,
  },
  placeholder: {
    fontSize: 16,
  },
  paper: {
    boxShadow: `${theme.shadows[1]} !important`,
    position: 'absolute',
    zIndex: 4,
    marginTop: theme.spacing(1),
    left: 0,
    right: 0,
    borderRadius: '10px',
  },
  warningMessage: {
    color: theme.palette.secondary.main,
  },
  readOnlyField: {
    width: '100%',
    minHeight: '56px',
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    border: '1px solid #d3d3d3',
    borderRadius: '25px',
    boxSizing: 'border-box',
  },
  readOnlyFieldMuted: {
    color: '#7a7a7a',
  },
  actions: {
    marginTop: '16px',
  },
  btn: {
    minWidth: '140px',
  },
}));

const getLabels = values =>
  (Array.isArray(values) ? values : values ? [values] : [])
    .map(item => (typeof item === 'string' ? item : item?.label || item?.name || ''))
    .filter(Boolean);

const JobRolesControl = ({
  label,
  options,
  value,
  onChange,
  onBlur,
  meta,
  warningMessage,
  isDisabled,
  onWrapperClick,
  placeholder,
  readOnly,
  readOnlyValues,
  emptyLabel,
  actionLabel,
  onAction,
  actionDisabled,
}) => {
  const classes = useStyles();
  const serverError = meta && !meta.dirtySinceLastSubmit && meta.submitError;
  const isError = meta && (meta.error || serverError) && meta.touched;
  const isWarning = warningMessage && isDisabled;
  const errorMessage = isError && (meta.error || serverError);
  const warningTextComponent = <span className={classes.warningMessage}>{warningMessage}</span>;

  if (readOnly) {
    const labels = getLabels(readOnlyValues);

    return (
      <>
        <Typography gutterBottom variant="subtitle2">
          {label}
        </Typography>
        <Box className={`${classes.readOnlyField} ${labels.length ? '' : classes.readOnlyFieldMuted}`}>
          {labels.length ? labels.join(', ') : emptyLabel}
        </Box>
        {actionLabel && onAction ? (
          <Box className={classes.actions}>
            <Button
              className={classes.btn}
              type="button"
              variant="outlined"
              color="primary"
              onClick={onAction}
              disabled={actionDisabled}
            >
              {actionLabel}
            </Button>
          </Box>
        ) : null}
      </>
    );
  }

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <div className={classes.root} tabIndex="0" role="button" onClick={onWrapperClick}>
      <ReactSelect
        classes={classes}
        textFieldProps={{
          label,
          error: isError,
          helperText: errorMessage || (isWarning && warningTextComponent),
          InputLabelProps: {
            shrink: true,
          },
        }}
        components={selectComponents}
        isClearable
        isDisabled={isDisabled}
        isMulti
        options={options}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder || label}
      />
    </div>
  );
};

JobRolesControl.defaultProps = {
  options: [],
  value: [],
  onChange: () => {},
  onBlur: () => {},
  meta: null,
  warningMessage: undefined,
  isDisabled: false,
  onWrapperClick: () => {},
  placeholder: '',
  readOnly: false,
  readOnlyValues: [],
  emptyLabel: '',
  actionLabel: '',
  onAction: undefined,
  actionDisabled: false,
};

JobRolesControl.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  value: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string,
      }),
    ),
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    }),
  ]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  meta: PropTypes.shape({
    dirtySinceLastSubmit: PropTypes.bool,
    submitError: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    touched: PropTypes.bool,
  }),
  warningMessage: PropTypes.string,
  isDisabled: PropTypes.bool,
  onWrapperClick: PropTypes.func,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  readOnlyValues: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        label: PropTypes.string,
        name: PropTypes.string,
      }),
    ]),
  ),
  emptyLabel: PropTypes.string,
  actionLabel: PropTypes.string,
  onAction: PropTypes.func,
  actionDisabled: PropTypes.bool,
};

export default JobRolesControl;
